import {
  Classes,
  OverlayToaster,
  OverlayToasterProps,
  Position,
  ToastOptions,
  ToastProps,
  Toaster,
  ToasterPosition,
} from "@blueprintjs/core";
import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import { useIsSignedEmbeddingApp } from "../../route/routeHooks.js";
import { ThemeOverrideWrapper } from "../../ThemeWrapper.js";

export const ToastIcon = styled.div`
  padding: 0 0 0 15px;

  font-size: 24px;

  /*
  This hides the default close button (which doesn't seem disable-able) in any other way.
  I needed to center the button vertically and decided the only way to do that was either use the
  <Toast /> component and wrap that in css, or do this.
  */
  & ~ .${Classes.BUTTON_GROUP} {
    display: none;
  }
`;

export const ToastBody = styled.div`
  display: flex;
  align-items: center;

  background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
`;

export const ToastContent = styled.div`
  margin-right: 50px;
`;

export const ToastContentTitle = styled.div`
  margin-bottom: 2px;

  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
`;

export const ToastContentDescription = styled.div`
  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

export interface HexToastProps extends ToastProps {
  /**
   * Where to show the given toast
   *
   * @default Position.TOP
   */
  position?: ToasterPosition;
}

export interface HexToasterInstance extends OverlayToaster {
  show: (props: HexToastProps, key?: string) => string;
}

export class HexToaster {
  constructor(
    private toasters: {
      [K in ToasterPosition]: Toaster;
    },
  ) {}

  public show: HexToasterInstance["show"] = (
    { position = Position.TOP, ...props },
    key,
  ) => {
    return this.toasters[position].show(props, key);
  };

  public dismiss: HexToasterInstance["dismiss"] = (key: string) => {
    for (const toaster of Object.values(this.toasters)) {
      toaster.dismiss(key);
    }
  };

  public clear: HexToasterInstance["clear"] = (...args) => {
    for (const toaster of Object.values(this.toasters)) {
      toaster.clear(...args);
    }
  };

  public getToasts: HexToasterInstance["getToasts"] = (...args) => {
    const options: ToastOptions[] = [];
    for (const toaster of Object.values(this.toasters)) {
      options.push(...toaster.getToasts(...args));
    }
    return options;
  };
}

/* eslint-disable tree-shaking/no-side-effects-in-initialization */
const DefaultToaster = new HexToaster({
  [Position.TOP_LEFT]: OverlayToaster.create({ position: Position.TOP_LEFT }),
  [Position.TOP]: OverlayToaster.create({ position: Position.TOP }),
  [Position.TOP_RIGHT]: OverlayToaster.create({ position: Position.TOP_RIGHT }),
  [Position.BOTTOM_RIGHT]: OverlayToaster.create({
    position: Position.BOTTOM_RIGHT,
  }),
  [Position.BOTTOM]: OverlayToaster.create({ position: Position.BOTTOM }),
  [Position.BOTTOM_LEFT]: OverlayToaster.create({
    position: Position.BOTTOM_LEFT,
  }),
});
/* eslint-enable tree-shaking/no-side-effects-in-initialization */

const ToasterContext = createContext<HexToaster>(DefaultToaster);

export function useToaster(): HexToaster {
  return useContext(ToasterContext);
}

export interface ToasterProviderProps extends Omit<
  OverlayToasterProps,
  "position"
> {
  children?: ReactNode;
}

export const ToasterProvider: React.ComponentType<ToasterProviderProps> =
  React.memo(function ToasterProvider({ children, ...toasterProps }) {
    const isSignedEmbeddingApp = useIsSignedEmbeddingApp();
    const [topLeftToaster, setTopLeftToaster] =
      useState<HexToasterInstance | null>(null);
    const [topToaster, setTopToaster] = useState<HexToasterInstance | null>(
      null,
    );
    const [topRightToaster, setTopRightToaster] =
      useState<HexToasterInstance | null>(null);
    const [bottomLeftToaster, setBottomLeftToaster] =
      useState<HexToasterInstance | null>(null);
    const [bottomToaster, setBottomToaster] =
      useState<HexToasterInstance | null>(null);
    const [bottomRightToaster, setBottomRightToaster] =
      useState<HexToasterInstance | null>(null);

    const toaster = useMemo(
      () =>
        topLeftToaster &&
        topToaster &&
        topRightToaster &&
        bottomLeftToaster &&
        bottomToaster &&
        bottomRightToaster
          ? new HexToaster({
              [Position.TOP_LEFT]: topLeftToaster,
              [Position.TOP]: topToaster,
              [Position.TOP_RIGHT]: topRightToaster,
              [Position.BOTTOM_LEFT]: bottomLeftToaster,
              [Position.BOTTOM]: bottomToaster,
              [Position.BOTTOM_RIGHT]: bottomRightToaster,
            })
          : undefined,
      [
        bottomLeftToaster,
        bottomRightToaster,
        bottomToaster,
        topLeftToaster,
        topRightToaster,
        topToaster,
      ],
    );

    return (
      <ToasterContext.Provider value={toaster ?? DefaultToaster}>
        <ThemeOverrideWrapper theme={isSignedEmbeddingApp ? "app" : "base"}>
          <OverlayToaster
            {...toasterProps}
            ref={setTopLeftToaster}
            position={Position.TOP_LEFT}
          />
          <OverlayToaster
            {...toasterProps}
            ref={setTopToaster}
            position={Position.TOP}
          />
          <OverlayToaster
            {...toasterProps}
            ref={setTopRightToaster}
            position={Position.TOP_RIGHT}
          />
          <OverlayToaster
            {...toasterProps}
            ref={setBottomLeftToaster}
            position={Position.BOTTOM_LEFT}
          />
          <OverlayToaster
            {...toasterProps}
            ref={setBottomToaster}
            position={Position.BOTTOM}
          />
          <OverlayToaster
            {...toasterProps}
            ref={setBottomRightToaster}
            position={Position.BOTTOM_RIGHT}
          />
        </ThemeOverrideWrapper>
        {children}
      </ToasterContext.Provider>
    );
  });
